import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSelector, useDispatch } from "react-redux";
import { setDrawer } from "../../redux-store/features/layoutSlice";

export default function UserDetailsDrawer({ details, onClose }) {
  const dispatch = useDispatch();
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);

  const closeDrawer = () => {
    if (onClose) {
      onClose();
    }
    dispatch(setDrawer(false));
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    // Try to parse the date, return "-" if invalid
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    // Format date as "March 5, 2025"
    try {
      return date.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  // Helper function to format field label
  const formatFieldLabel = (key) => {
    // Remove common prefixes/suffixes
    let label = key.replace(/_date_time$/, "").replace(/^is_/, "");

    // Split by underscores and capitalize each word
    return label
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters in camelCase
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to format field value
  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "-";

    // Handle boolean values
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Handle date fields - check for common date field patterns
    if (
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("created") ||
      key.toLowerCase().includes("updated") ||
      key.toLowerCase().includes("timestamp")
    ) {
      return formatDate(value);
    }

    // Handle special cases
    if (key === "is_active" || key.toLowerCase().includes("status")) {
      return value ? "Active" : "Inactive";
    }

    return value.toString();
  };

  // Generate dynamic details array from the data
  const generateUserDetails = (details) => {
    if (!details) return [];

    // Define field order priority with common field patterns
    const fieldOrder = [
      "name",
      "full_name",
      "first_name",
      "last_name",
      "email",
      "phone",
      "mobile",
      "contact",
      "status",
      "is_active",
      "company",
      "organization",
      "created",
      "updated",
      "create_date",
      "update_date",
      "timestamp",
    ];

    // Function to check if a field is ID-related
    const isIdField = (key) => {
      const normalizedKey = key.toLowerCase();
      return (
        normalizedKey.includes("id") ||
        normalizedKey.includes("_key") ||
        normalizedKey.includes("uuid") ||
        normalizedKey.includes("guid")
      );
    };

    // Function to find matching priority
    const getPriority = (key) => {
      const normalizedKey = key.toLowerCase();
      return fieldOrder.findIndex((pattern) =>
        normalizedKey.includes(pattern.toLowerCase())
      );
    };

    // Sort fields based on priority, then alphabetically
    const sortedKeys = Object.keys(details)
      .filter(
        (key) =>
          !isIdField(key) &&
          !key.toLowerCase().includes("password") &&
          !key.toLowerCase().includes("token")
      )
      .sort((a, b) => {
        const priorityA = getPriority(a);
        const priorityB = getPriority(b);

        if (priorityA === -1 && priorityB === -1) return a.localeCompare(b);
        if (priorityA === -1) return 1;
        if (priorityB === -1) return -1;
        return priorityA - priorityB;
      });

    return sortedKeys.map((key) => ({
      label: formatFieldLabel(key),
      value: formatFieldValue(key, details[key]),
      key: key,
    }));
  };

  const userDetails = generateUserDetails(details);

  return (
    <Dialog
      open={isDrawerOpen}
      onClose={closeDrawer}
      className="fixed inset-0 z-10"
    >
      <DialogBackdrop className="fixed inset-0 bg-transparent bg-opacity-40 backdrop-blur-sm backdrop-filter" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="fixed inset-y-0 right-0 flex max-w-full">
            <DialogPanel className="w-screen max-w-md bg-white shadow-lg">
              <div className="relative flex items-center p-4 border-b bg-indigo-600 text-white h-28">
                <DialogTitle className="text-lg font-bold flex-grow">
                  User Details
                </DialogTitle>
                <button
                  className="absolute top-4 right-4 text-white hover:text-gray-200"
                  onClick={closeDrawer}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {!details ? (
                <div className="p-4">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              ) : (
                <div className="px-4 mt-3">
                  {userDetails.map((detail) => (
                    <div
                      key={detail.key}
                      className="border-b border-gray-100 py-3 flex justify-between"
                    >
                      <h4 className="font-semibold text-sm text-gray-500">
                        {detail.label}
                      </h4>
                      <p className="text-sm font-semibold">{detail.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
